import { useMemo, useState } from 'react';
import { Archive, GripVertical, Loader2, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    useCustomFieldsCatalog,
    useCreateCustomField,
    useUpdateCustomField,
    useDeleteCustomField,
    useRestoreCustomField,
    useReorderCustomFields,
    type CustomFieldDef,
    type CustomFieldSubject,
    type CustomFieldType,
    type CustomFieldWritePayload,
} from '@/api/customFields';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { FormField, Input } from '@/components/ui/FormField';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
const FIELD_TYPES: { value: CustomFieldType; label: string; usesOptions: boolean }[] = [
    { value: 'text',        label: 'Text (single line)',   usesOptions: false },
    { value: 'textarea',    label: 'Text (multi-line)',    usesOptions: false },
    { value: 'number',      label: 'Number',               usesOptions: false },
    { value: 'date',        label: 'Date',                 usesOptions: false },
    { value: 'checkbox',    label: 'Checkbox (yes/no)',    usesOptions: false },
    { value: 'select',      label: 'Select (single)',      usesOptions: true  },
    { value: 'multiselect', label: 'Select (multiple)',    usesOptions: true  },
];

const FIELD_TYPE_LABEL: Record<CustomFieldType, string> = FIELD_TYPES.reduce(
    (acc, t) => ({ ...acc, [t.value]: t.label }),
    {} as Record<CustomFieldType, string>,
);

const SECTION_META: Record<CustomFieldSubject, { title: string; description: string }> = {
    client: {
        title: 'Custom Client Fields',
        description:
            'Extra fields shown on the client record. Values live on the client row and export via {client.cf.<slug>} placeholders.',
    },
    tx_plan: {
        title: 'Custom Treatment Plan Fields',
        description:
            'Extra fields shown on treatment plans. Values live on the plan and export via {plan.cf.<slug>} placeholders — useful for supervision schedule, parent-training cadence, etc.',
    },
};

function CustomFieldsPanel({ subject }: { subject: CustomFieldSubject }) {
    const { title, description } = SECTION_META[subject];
    const [showArchived, setShowArchived] = useState(false);
    const { data, isLoading, isError, error } = useCustomFieldsCatalog(showArchived);

    const createM  = useCreateCustomField(subject);
    const updateM  = useUpdateCustomField(subject);
    const deleteM  = useDeleteCustomField(subject);
    const restoreM = useRestoreCustomField(subject);
    const reorderM = useReorderCustomFields(subject);

    const [editing,    setEditing]    = useState<CustomFieldDef | null>(null);
    const [creating,   setCreating]   = useState(false);
    const [confirming, setConfirming] = useState<CustomFieldDef | null>(null);

    const defs = data ? data[subject] : [];

    const { activeRows, archivedRows } = useMemo(() => {
        const sortBy = (a: CustomFieldDef, b: CustomFieldDef) =>
            a.sortOrder - b.sortOrder || a.label.localeCompare(b.label);
        return {
            activeRows:   defs.filter((d) => !d.archivedAt).sort(sortBy),
            archivedRows: defs.filter((d) =>  d.archivedAt).sort(sortBy),
        };
    }, [defs]);

    // Active rows first, then archived pinned to the bottom when the
    // "Show archived" toggle is on.
    const rows = showArchived ? [...activeRows, ...archivedRows] : activeRows;

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    function handleDragEnd(e: DragEndEvent) {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIndex = activeRows.findIndex((r) => r.id === active.id);
        const newIndex = activeRows.findIndex((r) => r.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const nextIds = arrayMove(activeRows, oldIndex, newIndex).map((r) => r.id);
        reorderM.mutate(nextIds);
    }

    return (
        <div>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
                <h2 className="font-display text-lg font-bold text-text">{title}</h2>
                <div className="flex items-center gap-3 shrink-0">
                    <label className="flex items-center gap-1.5 text-xs text-text-muted cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                        />
                        Show archived
                    </label>
                    <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                        <Plus className="w-3.5 h-3.5" /> Add field
                    </Button>
                </div>
            </div>
            <p className="text-xs text-text-subtle mb-4">{description}</p>

            {isError ? (
                <Card className="p-8 text-center text-error">
                    <p className="font-medium mb-1">Couldn't load custom fields</p>
                    <p className="text-xs text-text-subtle">
                        {extractErrorMessage(error, 'Please try again.')}
                    </p>
                </Card>
            ) : (
                <Card className="p-0 overflow-hidden">
                    {isLoading ? (
                        <div className="p-10 flex items-center justify-center text-text-subtle">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-10 text-center text-text-subtle text-sm">
                            No custom fields yet. Click <b>Add field</b> to create one.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                        <th className="w-8" aria-label="Drag handle" />
                                        <th className="px-3 py-2 font-bold">Label</th>
                                        <th className="px-3 py-2 font-bold">Type</th>
                                        <th className="px-3 py-2 font-bold">Options</th>
                                        <th className="px-3 py-2 font-bold w-20">Required</th>
                                        <th className="px-3 py-2 font-bold w-24">Status</th>
                                        <th className="px-3 py-2 font-bold w-32 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={activeRows.map((r) => r.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <tbody>
                                            {rows.map((d) => (
                                                <CustomFieldRow
                                                    key={d.id}
                                                    row={d}
                                                    draggable={!d.archivedAt}
                                                    onEdit={() => setEditing(d)}
                                                    onArchive={() => setConfirming(d)}
                                                    onRestore={() => restoreM.mutate(d.id)}
                                                    restoring={restoreM.isPending}
                                                />
                                            ))}
                                        </tbody>
                                    </SortableContext>
                                </DndContext>
                            </table>
                        </div>
                    )}
                </Card>
            )}

            {(creating || editing) && (
                <CustomFieldDialog
                    initial={editing}
                    subjectLabel={title}
                    onClose={() => {
                        setCreating(false);
                        setEditing(null);
                    }}
                    onSubmit={async (payload) => {
                        if (editing) await updateM.mutateAsync({ id: editing.id, payload });
                        else await createM.mutateAsync(payload);
                    }}
                />
            )}

            {confirming && (
                <ArchiveConfirmDialog
                    row={confirming}
                    onClose={() => setConfirming(null)}
                    onConfirm={async () => {
                        await deleteM.mutateAsync(confirming.id);
                        setConfirming(null);
                    }}
                />
            )}
        </div>
    );
}

function CustomFieldRow({
    row,
    draggable,
    onEdit,
    onArchive,
    onRestore,
    restoring,
}: {
    row: CustomFieldDef;
    draggable: boolean;
    onEdit: () => void;
    onArchive: () => void;
    onRestore: () => void;
    restoring: boolean;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: row.id,
        disabled: !draggable,
    });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
    };
    const archived = !!row.archivedAt;

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={cn(
                'border-b border-border last:border-b-0 transition-colors',
                !isDragging && 'hover:bg-surface-muted',
                isDragging && 'bg-surface-muted',
                archived && 'opacity-60',
            )}
        >
            <td className="w-8 pl-2 align-middle">
                {draggable ? (
                    <button
                        type="button"
                        aria-label={`Reorder ${row.label}`}
                        className="p-1 text-text-subtle hover:text-text cursor-grab active:cursor-grabbing touch-none"
                        {...attributes}
                        {...listeners}
                    >
                        <GripVertical className="w-3.5 h-3.5" />
                    </button>
                ) : (
                    <span className="inline-block w-5" />
                )}
            </td>
            <td className="px-3 py-2 font-medium text-text">{row.label}</td>
            <td className="px-3 py-2 text-text-muted">{FIELD_TYPE_LABEL[row.type] ?? row.type}</td>
            <td className="px-3 py-2 text-text-muted">
                {row.options.length > 0 ? (
                    <span className="line-clamp-1" title={row.options.join(', ')}>
                        {row.options.join(', ')}
                    </span>
                ) : (
                    '—'
                )}
            </td>
            <td className="px-3 py-2 text-text-muted">{row.required ? 'Yes' : 'No'}</td>
            <td className="px-3 py-2">
                {archived ? <Badge variant="neutral">Archived</Badge> : <Badge variant="success">Active</Badge>}
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap">
                {archived ? (
                    <Button
                        variant="ghost"
                        size="xs"
                        aria-label={`Restore ${row.label}`}
                        onClick={onRestore}
                        disabled={restoring}
                    >
                        <RotateCcw className="w-3 h-3" /> Restore
                    </Button>
                ) : (
                    <>
                        <Button
                            variant="ghost"
                            size="xs"
                            aria-label={`Edit ${row.label}`}
                            onClick={onEdit}
                            className="mr-1"
                        >
                            <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="xs"
                            aria-label={`Archive ${row.label}`}
                            onClick={onArchive}
                        >
                            <Trash2 className="w-3 h-3" />
                        </Button>
                    </>
                )}
            </td>
        </tr>
    );
}

export function ClientCustomFieldsPanel()        { return <CustomFieldsPanel subject="client"  />; }
export function TreatmentPlanCustomFieldsPanel() { return <CustomFieldsPanel subject="tx_plan" />; }

function CustomFieldDialog({
    initial,
    subjectLabel,
    onClose,
    onSubmit,
}: {
    initial: CustomFieldDef | null;
    subjectLabel: string;
    onClose: () => void;
    onSubmit: (payload: CustomFieldWritePayload) => Promise<void>;
}) {
    const [label, setLabel]         = useState(initial?.label ?? '');
    const [type, setType]           = useState<CustomFieldType>(initial?.type ?? 'text');
    const [optionsText, setOptions] = useState((initial?.options ?? []).join('\n'));
    const [required, setRequired]   = useState(initial?.required ?? false);
    const [saving, setSaving]       = useState(false);
    const [err, setErr]             = useState<string | null>(null);

    const usesOptions = FIELD_TYPES.find((t) => t.value === type)?.usesOptions ?? false;

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        const trimmedLabel = label.trim();
        if (!trimmedLabel) {
            setErr('Label is required.');
            return;
        }
        const options = usesOptions
            ? optionsText.split('\n').map((s) => s.trim()).filter(Boolean)
            : [];
        if (usesOptions && options.length === 0) {
            setErr('Select / Multi-select fields need at least one option.');
            return;
        }

        setSaving(true);
        try {
            await onSubmit({
                label:    trimmedLabel,
                type,
                options,
                required,
            });
            onClose();
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>
                        {initial ? `Edit field — ${subjectLabel}` : `New field — ${subjectLabel}`}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <FormField label="Label" required error={err ?? undefined}>
                        {(id) => (
                            <Input
                                id={id}
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                placeholder="e.g. Supervision schedule"
                            />
                        )}
                    </FormField>
                    <FormField label="Type" required>
                        {(id) => (
                            <select
                                id={id}
                                value={type}
                                onChange={(e) => setType(e.target.value as CustomFieldType)}
                                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                            >
                                {FIELD_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        )}
                    </FormField>
                    {usesOptions && (
                        <FormField label="Options (one per line)" required>
                            {(id) => (
                                <textarea
                                    id={id}
                                    value={optionsText}
                                    onChange={(e) => setOptions(e.target.value)}
                                    rows={4}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                    placeholder={'First option\nSecond option'}
                                />
                            )}
                        </FormField>
                    )}
                    <FormField label="Required">
                        {(id) => (
                            <label className="flex items-center gap-2 h-9 text-sm text-text-muted">
                                <input
                                    id={id}
                                    type="checkbox"
                                    checked={required}
                                    onChange={(e) => setRequired(e.target.checked)}
                                />
                                Must be filled in
                            </label>
                        )}
                    </FormField>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ArchiveConfirmDialog({
    row,
    onClose,
    onConfirm,
}: {
    row: CustomFieldDef;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Archive custom field?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    Archive <span className="font-medium text-text">{row.label}</span>?
                    It will disappear from new record forms, but any values already
                    stored under this field stay on existing records — toggle
                    <b> Show archived</b> to restore it later.
                </p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try {
                                await onConfirm();
                            } catch (e) {
                                setErr(extractErrorMessage(e, 'Could not archive.'));
                            } finally {
                                setWorking(false);
                            }
                        }}
                    >
                        <Archive className="w-3.5 h-3.5" />
                        {working ? 'Archiving…' : 'Archive'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
